import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Lock, Mail, Phone, MapPin } from 'lucide-react';
import { SafeHtml } from './SafeHtml';
import { formatFairDate, venueLine } from './fairApi';
import './jobfair.css';

export const RegisterButton = ({ fair, kind, label, variant = 'primary', size = 'md', testId }) => {
  const open = kind === 'candidate' ? fair.candidate_open : fair.employer_open;
  const pad = size === 'lg' ? 'px-7 py-3.5 text-base' : size === 'sm' ? 'px-3 py-2 text-xs sm:text-sm' : 'px-5 py-2.5 text-sm';
  const styles = variant === 'primary'
    ? 'bg-[var(--jf-primary)] hover:bg-[var(--jf-primary-dark)] text-white shadow-lg shadow-[#E05A47]/25'
    : variant === 'light'
      ? 'bg-white text-[var(--jf-navy)] hover:bg-orange-50'
      : 'bg-[var(--jf-navy)] hover:bg-slate-800 text-white';
  const base = `inline-flex items-center justify-center gap-2 rounded-full font-semibold whitespace-nowrap ${pad}`;
  if (!open) {
    return (
      <span data-testid={testId} aria-disabled="true" title="Registrations are closed"
        className={`${base} bg-slate-200 text-slate-500 cursor-not-allowed select-none`}>
        <Lock className="w-3.5 h-3.5" /> {label}
      </span>
    );
  }
  return (
    <Link data-testid={testId} to={`/jobfairs/${fair.slug}/register/${kind}`}
      className={`${base} ${styles} transition-[transform,background-color] duration-200 hover:-translate-y-0.5 active:scale-95`}>
      {label}
    </Link>
  );
};

export const CompletedBanner = ({ fair }) => (
  <div data-testid="jobfair-completed-status-banner"
    className="bg-slate-900 text-slate-100 py-3 px-4 text-center text-sm sm:text-base font-semibold flex items-center justify-center gap-2 jf-noprint">
    <Lock className="w-4 h-4 text-amber-400 shrink-0" />
    <span><span className="text-amber-400 tracking-widest">COMPLETED</span> — {fair.name} has concluded. Registrations are now closed. Thank you!</span>
  </div>
);

export const FairHeader = ({ fair }) => (
  <header data-testid="jobfair-header" className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200/70 jf-noprint">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-3">
      <Link to={`/jobfairs/${fair.slug}`} data-testid="jobfair-header-home-link" className="flex items-center gap-3 min-w-0">
        {fair.logo && <img src={fair.logo} alt={fair.name} className="h-10 sm:h-12 w-auto object-contain" />}
        <span className="jf-heading text-base sm:text-xl font-extrabold tracking-tight text-[var(--jf-navy)] line-clamp-1 hidden sm:block">{fair.name}</span>
      </Link>
      <div className="flex items-center gap-2 sm:gap-3">
        <RegisterButton fair={fair} kind="candidate" label="Job Seeker" size="sm" testId="jobfair-header-seeker-register-button" />
        <RegisterButton fair={fair} kind="employer" label="Employer" size="sm" variant="dark" testId="jobfair-header-employer-register-button" />
      </div>
    </div>
  </header>
);

const LogoWall = ({ title, items, testId }) => (
  <div data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">{title}</p>
    <div className="flex flex-wrap gap-3">
      {items.map((o, i) => {
        const inner = (
          <div className="h-16 min-w-[7rem] px-4 rounded-xl bg-white flex items-center justify-center gap-2 transition-transform duration-200 hover:-translate-y-1">
            {o.logo ? <img src={o.logo} alt={o.name} className="max-h-12 max-w-[8rem] object-contain" /> : <span className="text-slate-800 text-sm font-bold">{o.name}</span>}
          </div>
        );
        return o.url ? <a key={i} href={o.url} target="_blank" rel="noreferrer" title={o.name}>{inner}</a> : <div key={i} title={o.name}>{inner}</div>;
      })}
    </div>
  </div>
);

export const FairFooter = ({ fair }) => {
  const partners = (fair.partners || []).filter((p) => (p.category || 'Partner') === 'Partner');
  const sponsors = (fair.partners || []).filter((p) => p.category === 'Sponsor');
  return (
    <footer data-testid="jobfair-footer" className="bg-[#0B0A1F] text-slate-300 pt-16 pb-8 jf-noprint">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4 space-y-4">
          <div className="flex items-center gap-3">
            {fair.logo && <img src={fair.logo} alt="" className="h-12 w-12 object-contain bg-white rounded-xl p-1" />}
            <p className="jf-heading text-xl font-extrabold text-white">{fair.name}</p>
          </div>
          {fair.slogan && <p className="text-amber-300/90 font-medium">{fair.slogan}</p>}
          <div className="space-y-2 text-sm">
            {(formatFairDate(fair) || venueLine(fair)) && <p className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[var(--jf-primary)] shrink-0" />{[formatFairDate(fair), venueLine(fair)].filter(Boolean).join(' · ')}</p>}
            {fair.contact_email && <a href={`mailto:${fair.contact_email}`} className="flex gap-2 hover:text-white"><Mail className="w-4 h-4 text-[var(--jf-primary)]" />{fair.contact_email}</a>}
            {fair.contact_phone && <a href={`tel:${fair.contact_phone}`} className="flex gap-2 hover:text-white"><Phone className="w-4 h-4 text-[var(--jf-primary)]" />{fair.contact_phone}</a>}
          </div>
        </div>
        <div className="lg:col-span-8 space-y-8">
          {fair.organizers?.length > 0 && <LogoWall title="Organised by" items={fair.organizers} testId="jobfair-footer-organizers" />}
          {partners.length > 0 && <LogoWall title="Partners" items={partners} testId="jobfair-footer-partners" />}
          {sponsors.length > 0 && <LogoWall title="Sponsors" items={sponsors} testId="jobfair-footer-sponsors" />}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 pt-6 border-t border-white/10 text-sm text-slate-400 flex flex-col sm:flex-row gap-3 justify-between">
        <SafeHtml html={fair.footer_text} testId="jobfair-footer-declaration" />
        <p>© {new Date().getFullYear()} {fair.name}</p>
      </div>
    </footer>
  );
};

export const FairShell = ({ fair, children, title }) => (
  <div className="jf-root min-h-screen flex flex-col"
    style={{ ...(fair.theme_primary ? { '--jf-primary': fair.theme_primary, '--jf-primary-dark': fair.theme_primary } : {}), ...(fair.theme_secondary ? { '--jf-navy': fair.theme_secondary } : {}) }}>
    <Helmet>
      <title>{title || fair.name}</title>
      <meta name="description" content={fair.short_description || fair.slogan || fair.name} />
      <meta property="og:title" content={title || fair.name} />
      {fair.hero_image && <meta property="og:image" content={fair.hero_image} />}
    </Helmet>
    {fair.effective_status === 'completed' && <CompletedBanner fair={fair} />}
    <FairHeader fair={fair} />
    <main className="flex-1">{children}</main>
    <FairFooter fair={fair} />
  </div>
);

export const FairState = ({ loading, error }) => (
  <div className="jf-root min-h-screen flex items-center justify-center p-6" data-testid="jobfair-state">
    {loading ? (
      <div className="w-10 h-10 rounded-full border-4 border-orange-200 border-t-[var(--jf-primary)] animate-spin" />
    ) : (
      <div className="text-center">
        <p className="jf-heading text-3xl font-extrabold text-[var(--jf-navy)] mb-2">Job fair not found</p>
        <p className="text-slate-500">{error}</p>
      </div>
    )}
  </div>
);
